import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, filter, map, take } from 'rxjs';
import { Subscription, SubscriptionStage, SubscriptionStatus } from 'fakturnia-shared';
import { DateTime } from 'luxon';

/**
 * Serwis do zarządzania subskrypcją użytkownika
 */
@Injectable({
    providedIn: 'root'
})
export class SubscriptionService {
    private _subscription$: BehaviorSubject<Subscription | null> = new BehaviorSubject<Subscription | null>(null);
    private _isLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor() { }

    /**
     * Pobierz aktualną subskrypcję
     */
    public getSubscription(): Observable<Subscription | null> {
        return this._subscription$.asObservable();
    }

    /**
     * Pobierz subskrypcję po załadowaniu danych
     * Czeka aż dane zostaną załadowane przed emisją
     */
    public getSubscriptionWhenLoaded(): Observable<Subscription | null> {
        return this._isLoaded$.pipe(
            filter(isLoaded => isLoaded),
            take(1),
            map(() => this._subscription$.value)
        );
    }

    /**
     * Sprawdź czy dane subskrypcji zostały załadowane
     */
    public isLoaded(): Observable<boolean> {
        return this._isLoaded$.asObservable();
    }

    /**
     * Sprawdź synchronicznie czy dane zostały załadowane
     */
    public isLoadedSync(): boolean {
        return this._isLoaded$.value;
    }

    /**
     * Ustaw subskrypcję (wywoływane po pobraniu danych użytkownika)
     */
    public setSubscription(subscriptionData: any): void {
        if (subscriptionData) {
            const subscription = new Subscription({
                stage: subscriptionData.stage as SubscriptionStage,
                status: subscriptionData.status as SubscriptionStatus,
                validUntil: subscriptionData.validUntil || '',
                purchasedAt: subscriptionData.purchasedAt || '',
                isAutoRenewable: subscriptionData.isAutoRenewable || false,
                provider: subscriptionData.provider || null,
                paymentType: subscriptionData.paymentType || 'one_time',
                planId: subscriptionData.planId || null
            });
            this._subscription$.next(subscription);
        } else {
            this._subscription$.next(null);
        }
        this._isLoaded$.next(true);
    }

    /**
     * Oznacz jako załadowane (nawet bez subskrypcji - np. dla nowych użytkowników)
     */
    public markAsLoaded(): void {
        this._isLoaded$.next(true);
    }

    /**
     * Sprawdź czy użytkownik ma minimum subskrypcję START lub wyższą
     */
    public hasMinimumStart(): boolean {
        const subscription = this._subscription$.value;
        if (!subscription) {
            return false;
        }
        return subscription.hasMinimumStart();
    }

    /**
     * Sprawdź czy użytkownik ma minimum subskrypcję PRO lub wyższą
     */
    public hasMinimumPro(): boolean {
        const subscription = this._subscription$.value;
        if (!subscription) {
            return false;
        }
        return subscription.hasMinimumPro();
    }

    /**
     * Pobierz aktualną subskrypcję synchronicznie (snapshot)
     */
    public getSubscriptionSnapshot(): Subscription | null {
        return this._subscription$.value;
    }

    /**
     * Sprawdź czy subskrypcja jest aktywna
     */
    public isActive(): boolean {
        const subscription = this._subscription$.value;
        if (!subscription) {
            return false;
        }
        return subscription.isActive();
    }

    /**
     * Pobierz pozostałe dni subskrypcji
     */
    public daysRemaining(): number {
        const subscription = this._subscription$.value;
        if (!subscription) {
            return 0;
        }
        return subscription.daysRemaining();
    }

    /**
     * Pobierz pozostałe godziny subskrypcji (dla Stripe)
     */
    public hoursRemaining(): number {
        const subscription = this._subscription$.value;
        if (!subscription) {
            return 0;
        }
        return subscription.hoursRemaining();
    }

    /**
     * Wyczyść subskrypcję (np. przy wylogowaniu)
     */
    public clear(): void {
        this._subscription$.next(null);
        this._isLoaded$.next(false);
    }
}
